// ============================================================================
// MIND BUBBLE OVERLAY - Avatar thought bubbles scattered across the screen
// Bubbles fade in slowly, linger for ~60s, then fade out.
// Each bubble gets a unique slot that never changes after assignment.
// ============================================================================

import React, { useEffect, useState, useRef } from 'react';
import { useSecondScreenData } from '../../contexts/SecondScreenContext';
import type { ActiveMindBubble } from '../../contexts/SecondScreenContext';

const FADE_IN_DURATION_MS = 1200;
const FADE_OUT_DURATION_MS = 2000;
const FADE_OUT_BEFORE_CLEAR_MS = 58_000;

// Bubble dimensions in % of screen to use for overlap checking
const BUBBLE_W = 34;  // ~520px / 1920px
const BUBBLE_H = 22;  // ~240px / 1080px
const MARGIN = 2;     // minimum gap between bubbles (%)

function randomPosition(): { left: number; top: number } {
  return {
    left: 2 + Math.random() * (78 - BUBBLE_W),
    top:  2 + Math.random() * (96 - BUBBLE_H),
  };
}

function overlaps(a: { left: number; top: number }, b: { left: number; top: number }): boolean {
  return (
    a.left < b.left + BUBBLE_W + MARGIN &&
    a.left + BUBBLE_W + MARGIN > b.left &&
    a.top  < b.top  + BUBBLE_H + MARGIN &&
    a.top  + BUBBLE_H + MARGIN > b.top
  );
}

function findFreePosition(occupied: { left: number; top: number }[]): { left: number; top: number } | null {
  for (let attempt = 0; attempt < 100; attempt++) {
    const pos = randomPosition();
    if (!occupied.some(o => overlaps(pos, o))) return pos;
  }
  // No free slot found — caller should queue this bubble
  return null;
}

interface BubbleProps {
  bubble: ActiveMindBubble;
  position: { left: number; top: number };
  onDismiss: (id: string) => void;
  avatarColor: string;
  avatarImageUrl?: string;
  avatarEmoji?: string;
}

const SingleBubble: React.FC<BubbleProps> = ({ bubble, position, onDismiss, avatarColor, avatarImageUrl, avatarEmoji }) => {
  const [opacity, setOpacity] = useState(0);
  const [visibleChars, setVisibleChars] = useState(0);
  const typewriterRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fadeOutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fade in
  useEffect(() => {
    const raf = requestAnimationFrame(() => setOpacity(1));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Typewriter
  useEffect(() => {
    if (typewriterRef.current) clearInterval(typewriterRef.current);
    const { thought } = bubble;
    typewriterRef.current = setInterval(() => {
      setVisibleChars(prev => {
        if (prev >= thought.length) {
          if (typewriterRef.current) clearInterval(typewriterRef.current);
          return prev;
        }
        return prev + 1;
      });
    }, 35);
    return () => { if (typewriterRef.current) clearInterval(typewriterRef.current); };
  }, [bubble.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Schedule fade-out slightly before context clears
  useEffect(() => {
    fadeOutTimerRef.current = setTimeout(() => setOpacity(0), FADE_OUT_BEFORE_CLEAR_MS);
    dismissTimerRef.current = setTimeout(() => onDismiss(bubble.id), FADE_OUT_BEFORE_CLEAR_MS + FADE_OUT_DURATION_MS);
    return () => {
      if (fadeOutTimerRef.current) clearTimeout(fadeOutTimerRef.current);
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const glowStyle = {
    boxShadow: `0 0 24px 6px ${avatarColor}55, 0 0 60px 12px ${avatarColor}22`,
    borderColor: avatarColor,
  };

  const handleClick = () => {
    setOpacity(0);
    setTimeout(() => onDismiss(bubble.id), FADE_OUT_DURATION_MS);
  };

  return (
    <div
      className="absolute flex items-start gap-5 max-w-[520px]"
      style={{
        left: `${position.left.toFixed(1)}%`,
        top: `${position.top.toFixed(1)}%`,
        opacity,
        transition: `opacity ${opacity === 0 ? FADE_OUT_DURATION_MS : FADE_IN_DURATION_MS}ms ease`,
        pointerEvents: opacity > 0 ? 'auto' : 'none',
      }}
      onClick={handleClick}
    >
      {/* Avatar circle */}
      <div className="shrink-0 relative">
        <div
          className="w-32 h-32 rounded-full border-[4px] overflow-hidden transition-all duration-500"
          style={glowStyle}
        >
          {avatarImageUrl ? (
            <img src={avatarImageUrl} alt={bubble.avatarName} className="w-full h-full object-cover" />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center text-4xl font-bold"
              style={{ backgroundColor: avatarColor + '33', color: '#fff' }}
            >
              {avatarEmoji || bubble.avatarName.charAt(0)}
            </div>
          )}
        </div>
        <div
          className="absolute inset-0 rounded-full animate-pulse-ring"
          style={{ borderWidth: 3, borderStyle: 'solid', borderColor: avatarColor + '80' }}
        />
      </div>

      {/* Speech bubble */}
      <div className="flex-1 rounded-2xl bg-black/70 backdrop-blur-md border border-white/10 px-6 py-4 cursor-pointer mt-4">
        <p className="text-sm font-semibold mb-2 uppercase tracking-wider" style={{ color: avatarColor }}>
          {bubble.avatarName}
        </p>
        <p className="text-white/90 text-base leading-relaxed">
          {bubble.thought.slice(0, visibleChars)}
          {visibleChars < bubble.thought.length && (
            <span className="inline-block w-[2px] h-[1em] bg-white/60 ml-[1px] animate-pulse align-middle" />
          )}
        </p>
        <p className="text-[11px] text-white/25 italic mt-3">thinking out loud...</p>
      </div>
    </div>
  );
};

const MindBubbleOverlay: React.FC = () => {
  const { activeMindBubbles, clearMindBubble, state } = useSecondScreenData();
  const [, forceUpdate] = useState(0);

  // Stable position assignments — only bubbles with an assigned position are rendered.
  const positionsRef = useRef<Map<string, { left: number; top: number }>>(new Map());
  // Bubbles waiting for a free slot
  const waitingRef = useRef<string[]>([]);

  const tryPlaceWaiting = (bubbleMap: Map<string, ActiveMindBubble>) => {
    const stillWaiting: string[] = [];
    for (const id of waitingRef.current) {
      const bubble = bubbleMap.get(id);
      if (!bubble) continue; // already dismissed, drop it
      const occupied = Array.from(positionsRef.current.values());
      const pos = findFreePosition(occupied);
      if (pos !== null) {
        positionsRef.current.set(id, pos);
      } else {
        stillWaiting.push(id);
      }
    }
    waitingRef.current = stillWaiting;
  };

  // Build a quick lookup for this render
  const bubbleMap = new Map(activeMindBubbles.map(b => [b.id, b]));

  // Free positions for dismissed bubbles and retry waiting queue
  let anyFreed = false;
  for (const id of positionsRef.current.keys()) {
    if (!bubbleMap.has(id)) {
      positionsRef.current.delete(id);
      anyFreed = true;
    }
  }
  // Also drop waiting entries that were dismissed before placement
  waitingRef.current = waitingRef.current.filter(id => bubbleMap.has(id));

  if (anyFreed && waitingRef.current.length > 0) {
    tryPlaceWaiting(bubbleMap);
  }

  // Try to assign positions to new bubbles
  for (const bubble of activeMindBubbles) {
    if (positionsRef.current.has(bubble.id)) continue;
    if (waitingRef.current.includes(bubble.id)) continue;
    const occupied = Array.from(positionsRef.current.values());
    const pos = findFreePosition(occupied);
    if (pos !== null) {
      positionsRef.current.set(bubble.id, pos);
    } else {
      // No room — queue it until a slot frees up
      waitingRef.current.push(bubble.id);
    }
  }

  // When a bubble is dismissed from the overlay, free its slot and try to place waiting
  const handleDismiss = (id: string) => {
    clearMindBubble(id);
    positionsRef.current.delete(id);
    waitingRef.current = waitingRef.current.filter(i => i !== id);
    // Trigger re-render so waiting bubbles can claim the freed slot
    forceUpdate(n => n + 1);
  };

  const renderable = activeMindBubbles.filter(b => positionsRef.current.has(b.id));

  if (renderable.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[90] pointer-events-none">
      {renderable.map(bubble => {
        const avatar = state?.sessionAvatars.find(a => a.role === bubble.avatarRole);
        const position = positionsRef.current.get(bubble.id)!;
        return (
          <div key={bubble.id} className="pointer-events-auto">
            <SingleBubble
              bubble={bubble}
              position={position}
              onDismiss={handleDismiss}
              avatarColor={avatar?.primaryColor || '#00c7c4'}
              avatarImageUrl={avatar?.imageUrl}
              avatarEmoji={avatar?.emoji}
            />
          </div>
        );
      })}
    </div>
  );
};

export default MindBubbleOverlay;
